"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Typography } from "@/components/shared/typography/typography";
import { SearchBar, MARKETPLACE_SEARCH_PLACEHOLDER_TERMS } from "@/components/features/home/hero/components/SearchBar";
import { SearchDropdown } from "./listing/SearchDropdown";
import { useMarketplaceSearchSuggestions } from "./listing/useMarketplaceSearchSuggestions";
import type { MarketplaceSearchResult } from "@/types/api/marketplace";

interface MarketplaceSearchModalProps {
  searchValue: string;
  setSearchValue: (value: string) => void;
  onSearchCommit: (query: string) => void;
  onSearchSuggestionSelect?: (result: MarketplaceSearchResult) => void;
  /** Close sheet and dismiss mobile keyboard */
  onDismiss?: () => void;
  searchSuggestions: MarketplaceSearchResult[];
  debouncedSearch?: string;
  isSearchFetching?: boolean;
}

export default function MarketplaceSearchModal({
  searchValue,
  setSearchValue,
  onSearchCommit,
  onSearchSuggestionSelect,
  onDismiss,
  searchSuggestions,
  debouncedSearch = "",
  isSearchFetching = false,
}: MarketplaceSearchModalProps) {
  const [isFocused, setIsFocused] = useState(true);

  useEffect(() => {
    setIsFocused(true);
  }, []);

  const searchPanel = useMarketplaceSearchSuggestions({
    searchQuery: searchValue,
    debouncedSearch,
    suggestions: searchSuggestions,
    isFetching: isSearchFetching,
    isFocused,
  });

  return (
    <DialogContent
      className="min-h-screen max-w-full rounded-none px-0"
      showCloseButton={false}>
      <div className="sr-only">
        <DialogTitle>Search Experiences</DialogTitle>
      </div>
      <div>
        <div className="relative flex items-center justify-between">
          <Typography
            variant="p"
            className="leading-text-lg w-full p-6 text-center text-lg font-bold">
            Search Experiences
          </Typography>
          <DialogClose
            className="absolute top-4 right-4 z-100 w-fit rounded-full border border-gray-300 bg-white p-1.5 text-gray-900"
            aria-label="Close">
            <X />
          </DialogClose>
        </div>

        <SearchBar
          searchBarContainerClassName="mx-5 w-[90%]"
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          onSearchSubmit={onSearchCommit}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          iconSize={18}
          iconContainerClassName="h-9 w-9"
          placeholderTerms={MARKETPLACE_SEARCH_PLACEHOLDER_TERMS}
        />

        {searchPanel.showPanel && (
          <SearchDropdown
            variant="sheet"
            status={searchPanel.status}
            results={searchPanel.displayResults}
            relatedResults={searchPanel.displayRelated}
            searchQuery={searchPanel.activeQuery}
            onCommitSearch={onSearchCommit}
            onSuggestionSelect={(result) => {
              onSearchSuggestionSelect?.(result);
              onDismiss?.();
            }}
            onResultClick={onDismiss}
            className="pt-4"
          />
        )}
      </div>
    </DialogContent>
  );
}
